import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, 
  UserCheck, 
  Heart, 
  Baby, 
  Clock, 
  Building2, 
  Home, 
  User,
  ArrowRight,
  Shield,
  TrendingUp,
  PiggyBank,
  Calculator,
  FileText,
  CheckCircle
} from 'lucide-react';
import SEO, { SEOPages } from '../components/SEO';

const Guides = () => {
  const [selectedGuide, setSelectedGuide] = useState('etudiant');

  const lifeStages = [
    {
      id: 'etudiant',
      title: 'Vous êtes étudiant',
      icon: GraduationCap,
      color: 'from-blue-500 to-cyan-500',
      shortDesc: 'Premiers pas vers l\'indépendance financière',
      content: {
        mainTitle: 'Étudiant : préparez votre avenir financier',
        description: 'Même en tant qu\'étudiant, il n\'est jamais trop tôt pour commencer à penser à votre avenir financier. Découvrez nos conseils pour bien démarrer.',
        keyPoints: [
          {
            title: 'Ouvrir votre premier compte bancaire',
            description: 'Choisissez une banque avec des conditions avantageuses pour les étudiants et apprenez à gérer votre budget.',
            icon: Shield
          },
          {
            title: 'Comprendre les assurances obligatoires',
            description: 'Assurance maladie, responsabilité civile : découvrez vos obligations et optimisez vos couvertures.',
            icon: FileText
          },
          {
            title: 'Épargner même avec un petit budget',
            description: 'Quelques francs par mois peuvent faire la différence sur le long terme grâce aux intérêts composés.',
            icon: PiggyBank
          }
        ],
        cta: 'Commencer mon épargne étudiante'
      }
    },
    {
      id: 'vie-active',
      title: 'Vous entrez dans la vie active',
      icon: UserCheck,
      color: 'from-green-500 to-emerald-500',
      shortDesc: 'Optimisez votre premier salaire',
      content: {
        mainTitle: 'Vie active : maximisez vos premiers revenus',
        description: 'Votre premier emploi marque le début de votre indépendance financière. Apprenez à optimiser votre situation dès le départ.',
        keyPoints: [
          {
            title: 'Comprendre votre 2ème pilier',
            description: 'Prévoyance professionnelle obligatoire : comment elle fonctionne et comment l\'optimiser.',
            icon: Shield
          },
          {
            title: 'Démarrer votre 3ème pilier',
            description: 'Optimisez vos déductions fiscales tout en préparant sereinement votre retraite dès le début de votre carrière.',
            icon: TrendingUp
          },
          {
            title: 'Budgeter efficacement',
            description: 'Répartissez intelligemment vos revenus entre besoins, épargne et loisirs.',
            icon: Calculator
          }
        ],
        cta: 'Optimiser mes premiers revenus'
      }
    },
    {
      id: 'celibataire',
      title: 'Vous êtes célibataire',
      icon: User,
      color: 'from-purple-500 to-pink-500',
      shortDesc: 'Liberté financière et sécurité personnelle',
      content: {
        mainTitle: 'Célibataire : construisez votre patrimoine',
        description: 'En tant que célibataire, vous avez une flexibilité unique pour construire votre patrimoine. Profitez-en intelligemment.',
        keyPoints: [
          {
            title: 'Maximiser vos investissements',
            description: 'Profitez de votre flexibilité pour prendre des risques calculés et optimiser vos rendements.',
            icon: TrendingUp
          },
          {
            title: 'Protéger vos revenus',
            description: 'Assurance perte de gain et invalidité : essentielles quand vous n\'avez qu\'un seul revenu.',
            icon: Shield
          },
          {
            title: 'Optimiser vos impôts',
            description: 'Déductions fiscales, 3ème pilier : maximisez vos économies d\'impôts.',
            icon: Calculator
          }
        ],
        cta: 'Optimiser ma situation de célibataire'
      }
    },
    {
      id: 'en-couple',
      title: 'Vous êtes en couple',
      icon: Heart,
      color: 'from-red-500 to-rose-500',
      shortDesc: 'Planification financière à deux',
      content: {
        mainTitle: 'En couple : gérez vos finances à deux',
        description: 'Vivre à deux change la donne financière. Découvrez comment optimiser votre situation commune.',
        keyPoints: [
          {
            title: 'Choisir le bon régime matrimonial',
            description: 'Séparation de biens, communauté : quel impact sur vos finances et vos impôts ?',
            icon: FileText
          },
          {
            title: 'Optimiser vos assurances communes',
            description: 'Ménage commun, véhicules, responsabilité civile : économisez en optimisant vos couvertures.',
            icon: Shield
          },
          {
            title: 'Planifier ensemble',
            description: '3ème pilier, épargne, investissements : coordonnez vos stratégies pour maximiser les avantages.',
            icon: PiggyBank
          }
        ],
        cta: 'Optimiser nos finances de couple'
      }
    },
    {
      id: 'avec-enfants',
      title: 'Vous avez des enfants',
      icon: Baby,
      color: 'from-orange-500 to-yellow-500',
      shortDesc: 'Sécurisez l\'avenir de votre famille',
      content: {
        mainTitle: 'Parents : protégez l\'avenir de vos enfants',
        description: 'Avoir des enfants apporte joie et responsabilités. Découvrez comment sécuriser leur avenir financier.',
        keyPoints: [
          {
            title: 'Sécuriser le financement des études',
            description: 'Grâce à la prévoyance privée, le financement des études est garanti quoi qu\'il arrive.',
            icon: GraduationCap
          },
          {
            title: 'Maintenir un revenu stable',
            description: 'Assurance-vie et perte de gain : protégez votre famille en cas d\'accident ou de décès.',
            icon: Shield
          },
          {
            title: 'Combler les lacunes de prévoyance',
            description: 'Temps partiel ou interruption professionnelle : compensez la réduction des cotisations.',
            icon: PiggyBank
          }
        ],
        cta: 'Protéger ma famille'
      }
    },
    {
      id: 'temps-partiel',
      title: 'Travailler à temps partiel',
      icon: Clock,
      color: 'from-indigo-500 to-blue-500',
      shortDesc: 'Optimisez votre prévoyance malgré un revenu réduit',
      content: {
        mainTitle: 'Temps partiel : ne négligez pas votre prévoyance',
        description: 'Travailler à temps partiel peut créer des lacunes de prévoyance. Découvrez comment les combler.',
        keyPoints: [
          {
            title: 'Identifier les lacunes LPP',
            description: 'Le 2ème pilier est réduit proportionnellement. Calculez l\'impact sur votre retraite.',
            icon: Calculator
          },
          {
            title: 'Maximiser votre 3ème pilier',
            description: 'Compensez avec le 3ème pilier : jusqu\'à 7\'258 CHF déductibles fiscalement.',
            icon: TrendingUp
          },
          {
            title: 'Adapter vos assurances',
            description: 'Perte de gain, invalidité : ajustez vos couvertures à votre nouveau revenu.',
            icon: Shield
          }
        ],
        cta: 'Optimiser ma prévoyance temps partiel'
      }
    },
    {
      id: 'independant',
      title: 'Devenir indépendant',
      icon: Building2,
      color: 'from-teal-500 to-green-500',
      shortDesc: 'Gérez votre prévoyance d\'entrepreneur',
      content: {
        mainTitle: 'Indépendant : maîtrisez votre prévoyance',
        description: 'L\'indépendance offre liberté et responsabilités. Construisez une prévoyance solide pour votre avenir.',
        keyPoints: [
          {
            title: 'Organiser votre prévoyance',
            description: 'AVS obligatoire, 2ème pilier facultatif : optimisez votre couverture retraite.',
            icon: PiggyBank
          },
          {
            title: 'Protéger votre activité',
            description: 'Assurance perte de gain, responsabilité professionnelle : protégez votre business.',
            icon: Shield
          },
          {
            title: 'Optimiser fiscalement',
            description: 'Déductions professionnelles, rachats LPP : maximisez vos avantages fiscaux.',
            icon: Calculator
          }
        ],
        cta: 'Optimiser ma prévoyance d\'indépendant'
      }
    },
    {
      id: 'proprietaire',
      title: 'Devenir propriétaire',
      icon: Home,
      color: 'from-amber-500 to-orange-500',
      shortDesc: 'Financez intelligemment votre logement',
      content: {
        mainTitle: 'Propriétaire : financez votre rêve immobilier',
        description: 'Accéder à la propriété est un projet majeur. Découvrez toutes les clés pour réussir votre financement.',
        keyPoints: [
          {
            title: 'Optimiser votre financement',
            description: 'Hypothèques fixes, variables, SARON : choisissez la meilleure stratégie.',
            icon: Home
          },
          {
            title: 'Utiliser vos fonds de prévoyance',
            description: '2ème et 3ème pilier : comment les utiliser pour votre achat immobilier.',
            icon: PiggyBank
          },
          {
            title: 'Protéger votre investissement',
            description: 'Assurance bâtiment, responsabilité : protégez votre bien et votre famille.',
            icon: Shield
          }
        ],
        cta: 'Financer mon projet immobilier'
      }
    }
  ];

  const currentGuide = lifeStages.find(stage => stage.id === selectedGuide);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <SEO {...SEOPages.guides} />
      {/* Hero Section */}
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold heading-display text-white mb-6">
              Guides par <span className="text-gradient">étapes de vie</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Chaque étape de la vie apporte ses défis financiers. Découvrez nos conseils personnalisés 
              pour optimiser votre situation selon votre profil.
            </p>
          </div>
        </div>
      </section>

      {/* Life Stages Navigation */}
      <section className="pb-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {lifeStages.map((stage) => {
              const IconComponent = stage.icon;
              return (
                <button
                  key={stage.id}
                  onClick={() => setSelectedGuide(stage.id)}
                  className={`p-6 rounded-2xl transition-all duration-300 text-left group ${
                    selectedGuide === stage.id 
                      ? 'glass-card border-white/30 scale-105' 
                      : 'glass-card-subtle border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${stage.color} p-3 mb-4 group-hover:scale-110 transition-transform`}>
                    <IconComponent className="w-full h-full text-white" />
                  </div>
                  <h3 className="font-semibold text-white mb-2 text-sm md:text-base">
                    {stage.title}
                  </h3>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                    {stage.shortDesc}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Selected Guide Content */}
      {currentGuide && (
        <section className="pb-20">
          <div className="container mx-auto px-4">
            <div className="glass-card p-8 md:p-12 rounded-3xl">
              <div className="flex items-center mb-8">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${currentGuide.color} p-4 mr-6`}>
                  <currentGuide.icon className="w-full h-full text-white" />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    {currentGuide.content.mainTitle}
                  </h2>
                  <p className="text-gray-300 text-lg">
                    {currentGuide.content.description}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {currentGuide.content.keyPoints.map((point, index) => {
                  const IconComponent = point.icon;
                  return (
                    <div key={index} className="glass-card-subtle p-6 rounded-2xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-3 mb-4">
                        <IconComponent className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-3">
                        {point.title}
                      </h3>
                      <p className="text-gray-300 leading-relaxed">
                        {point.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="text-center">
                <Link
                  to="/contact"
                  className="btn-glass btn-primary inline-flex items-center"
                >
                  {currentGuide.content.cta}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Guides Détaillés Section */}
      <section className="pb-12">
        <div className="container mx-auto px-4">
          <div className="glass-card p-8 md:p-12 rounded-3xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                📚 Nos Guides Détaillés
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Explorez nos guides complets pour approfondir vos connaissances financières
              </p>
            </div>
            
            <div className="grid md:grid-cols-1 gap-6 max-w-3xl mx-auto">
              <Link
                to="/guide-3eme-pilier"
                className="glass p-6 rounded-2xl border border-white/20 hover:border-blue-500/50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-xl group-hover:scale-110 transition-transform">
                    <PiggyBank className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                      Guide Complet 3ème Pilier 2026
                    </h3>
                    <p className="text-gray-300 mb-3">
                      Tout ce que vous devez savoir pour optimiser votre prévoyance et réduire vos impôts à Neuchâtel
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                        Plafonds 2026
                      </span>
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">
                        3a vs 3b
                      </span>
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                        Optimisation fiscale
                      </span>
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-xs">
                        Cas pratiques
                      </span>
                    </div>
                    <div className="mt-4 flex items-center text-blue-400 font-semibold group-hover:gap-2 transition-all">
                      Lire le guide complet
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>

              <Link
                to="/guide-rachat-2eme-pilier"
                className="glass p-6 rounded-2xl border border-white/20 hover:border-green-500/50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-4 rounded-xl group-hover:scale-110 transition-transform">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-green-400 transition-colors">
                      Guide Rachat 2ème Pilier Suisse
                    </h3>
                    <p className="text-gray-300 mb-3">
                      Optimisez votre prévoyance professionnelle et économisez des milliers de francs d'impôts
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                        Économies fiscales majeures
                      </span>
                      <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs">
                        Stratégies de rachat
                      </span>
                      <span className="px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-xs">
                        Calcul optimal
                      </span>
                      <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-xs">
                        Timing parfait
                      </span>
                    </div>
                    <div className="mt-4 flex items-center text-green-400 font-semibold group-hover:gap-2 transition-all">
                      Lire le guide complet
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>

              <Link
                to="/guide-hypotheque-suisse"
                className="glass p-6 rounded-2xl border border-white/20 hover:border-orange-500/50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 p-4 rounded-xl group-hover:scale-110 transition-transform">
                    <Home className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">
                      Guide Hypothèque Suisse
                    </h3>
                    <p className="text-gray-300 mb-3">
                      Taux actuels, fonds propres, retrait 3ème pilier et optimisation de votre financement immobilier
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs">
                        Taux 2026
                      </span>
                      <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">
                        Fonds propres
                      </span>
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-xs">
                        Retrait 3a/LPP
                      </span>
                      <span className="px-3 py-1 bg-rose-500/20 text-rose-400 rounded-full text-xs">
                        Optimisation fiscale
                      </span>
                    </div>
                    <div className="mt-4 flex items-center text-orange-400 font-semibold group-hover:gap-2 transition-all">
                      Lire le guide complet
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>

              <Link
                to="/guide-planification-retraite-anticipee"
                className="glass p-6 rounded-2xl border border-white/20 hover:border-purple-500/50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-xl group-hover:scale-110 transition-transform">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                      Guide Retraite Anticipée Suisse
                    </h3>
                    <p className="text-gray-300 mb-3">
                      Stratégies financières, optimisation AVS/LPP, impact fiscal et tout pour partir avant 65 ans
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">
                        Retraite dès 58-60 ans
                      </span>
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-xs">
                        Impact AVS/LPP
                      </span>
                      <span className="px-3 py-1 bg-fuchsia-500/20 text-fuchsia-400 rounded-full text-xs">
                        Budget réaliste
                      </span>
                      <span className="px-3 py-1 bg-rose-500/20 text-rose-400 rounded-full text-xs">
                        Optimisation fiscale
                      </span>
                    </div>
                    <div className="mt-4 flex items-center text-purple-400 font-semibold group-hover:gap-2 transition-all">
                      Lire le guide complet
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>

              <Link
                to="/guide-prevoyance-independants"
                className="glass p-6 rounded-2xl border border-white/20 hover:border-purple-500/50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-4 rounded-xl group-hover:scale-110 transition-transform">
                    <Building2 className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                      Guide Prévoyance Indépendants Suisse
                    </h3>
                    <p className="text-gray-300 mb-3">
                      Plafonds 3a majorés (CHF 36'288), assurances essentielles et stratégies sans 2ème pilier
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">
                        Plafond CHF 36'288
                      </span>
                      <span className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-xs">
                        Assurances vitales
                      </span>
                      <span className="px-3 py-1 bg-fuchsia-500/20 text-fuchsia-400 rounded-full text-xs">
                        Économie CHF 13'000
                      </span>
                      <span className="px-3 py-1 bg-violet-500/20 text-violet-400 rounded-full text-xs">
                        Sans LPP
                      </span>
                    </div>
                    <div className="mt-4 flex items-center text-purple-400 font-semibold group-hover:gap-2 transition-all">
                      Lire le guide complet
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>

              <Link
                to="/guide-investissement-locatif"
                className="glass p-6 rounded-2xl border border-white/20 hover:border-yellow-500/50 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-gradient-to-br from-yellow-500 to-orange-600 p-4 rounded-xl group-hover:scale-110 transition-transform">
                    <Home className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                      Guide Investissement Locatif Suisse
                    </h3>
                    <p className="text-gray-300 mb-3">
                      Financement, fiscalité, rendement : tout pour réussir votre investissement immobilier locatif
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
                        Rendement locatif
                      </span>
                      <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs">
                        Optimisation fiscale
                      </span>
                      <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">
                        Financement PPE
                      </span>
                      <span className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs">
                        Cash-flow positif
                      </span>
                    </div>
                    <div className="mt-4 flex items-center text-yellow-400 font-semibold group-hover:gap-2 transition-all">
                      Lire le guide complet
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="glass-card p-8 md:p-12 rounded-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à optimiser votre situation ?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Quelle que soit votre étape de vie, nous avons les solutions pour vous accompagner. 
              Contactez-nous pour un premier conseil gratuit et sans engagement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-glass btn-primary inline-flex items-center justify-center"
              >
                Demander un conseil gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/a-propos"
                className="btn-glass btn-secondary inline-flex items-center justify-center"
              >
                Découvrir ATOM Group SA
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Guides;